import React, { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Box, Button, Text, TogglerCheckbox } from '@factorialco/deprecated-design-system'

import Callout from 'modules/core/components/Callout'
import { EmployeeForSelectFragmentNoAccess } from 'modules/core/components/EntitySelectWrapper/Entities/Employees/types'
import Modal from 'modules/core/components/Modal'
import Selector from 'modules/core/components/Selector'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import useLocalStorage from 'modules/core/lib/useLocalStorage'
import app from 'modules/core/store/models/app'

import { EmployeeSelector } from 'modules/employees/components/EmployeeSelector'
import { useCreateTrainingClassMembership } from 'modules/trainings/hooks/TrainingClassMemberships/useCreateTrainingClassMembership'
import { useTrainingClasses } from 'modules/trainings/hooks/TrainingClasses/useTrainingClasses'
import { useCreateTrainingMemberships } from 'modules/trainings/hooks/TrainingMemberships/useCreateTrainingMemberships'

export const AddParticipantsModal = ({ employeeIds }: { employeeIds: number[] }) => {
  const [toggled, setToggled] = useState<boolean>(false)
  const [calloutClosed, setCalloutClosed] = useState<boolean>(false)
  const [classIds, setClassIds] = useState<number[]>([])
  const { id } = useParams()
  const [selectedEmployees, setSelectedEmployees] = useState<EmployeeForSelectFragmentNoAccess[]>(
    []
  )
  const [showTerminatedEmployees, setShowTerminatedEmployees] = useLocalStorage<boolean>(
    'trainings-participants-showTerminatedEmployees',
    true
  )
  const { company } = useCurrentSession()
  const { trainingClasses } = useTrainingClasses({ trainingId: Number(id) })
  const { createTrainingClassMemberships } = useCreateTrainingClassMembership()
  const { createTrainingMemberships } = useCreateTrainingMemberships()
  const navigate = useNavigate()
  const { navigation } = useNavigation()

  const onSubmit = async () => {
    if (toggled && classIds.length > 0) {
      const response = await createTrainingClassMemberships({
        trainingClassIds: classIds,
        employeeIds: selectedEmployees.map((employee) => employee.id),
        companyId: company.id,
      })

      const handled = handleResult(response, () => {
        app.setFlash({
          type: 'wrong',
          message: i18n.t(
            'trainings.trainings_revamp.training_detail.employees_table.add_participants_modal.error'
          ),
        })
      })

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t(
            'trainings.trainings_revamp.training_detail.employees_table.add_participants_modal.success'
          ),
        })

        navigate(
          navigation.trainings.list.details_revamp.view.participants.fullPath({ id: Number(id) })
        )
      }
    } else {
      onCreateTrainingMemberships()
    }
  }
  const onCreateTrainingMemberships = async () => {
    const response = await createTrainingMemberships({
      employeeIds: selectedEmployees.map((employee) => employee.id),
      trainingId: Number(id),
    })

    const handled = handleResult(response, () => {
      app.setFlash({
        type: 'wrong',
        message: i18n.t(
          'trainings.trainings_revamp.training_detail.employees_table.add_participants_modal.error'
        ),
      })
    })

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t(
          'trainings.trainings_revamp.training_detail.employees_table.add_participants_modal.success'
        ),
      })

      navigate(
        navigation.trainings.list.details_revamp.view.participants.fullPath({ id: Number(id) })
      )
    }
  }

  return (
    <Modal
      title={i18n.t(
        'trainings.trainings_revamp.training_detail.employees_table.add_participants_modal.title'
      )}
      description={i18n.t(
        'trainings.trainings_revamp.training_detail.employees_table.add_participants_modal.description'
      )}
      onClose={() =>
        navigate(
          navigation.trainings.list.details_revamp.view.participants.fullPath({ id: Number(id) })
        )
      }
      footer={
        <Box gap="s8" flexDirection="row">
          <Button.Router
            to={{
              pathname: navigation.trainings.list.details_revamp.view.participants.fullPath({
                id: Number(id),
              }),
            }}
            type="secondary"
          >
            {i18n.t('common.cancel')}
          </Button.Router>
          <Button onClick={() => onSubmit()} type="negative">
            {i18n.t(
              'trainings.trainings_revamp.training_detail.employees_table.assign_modal.assign'
            )}
          </Button>
        </Box>
      }
    >
      <Box gap="s16">
        <EmployeeSelector
          alwaysOpen
          includeInactive={showTerminatedEmployees ?? true}
          onSelect={setSelectedEmployees}
          multiple
          value={selectedEmployees}
          excludeIds={employeeIds}
          allowedGroups={['all', 'teams', 'managers', 'workplaces']}
        />
        <Box flexDirection="row" gap="s8">
          <TogglerCheckbox
            value={showTerminatedEmployees ?? true}
            onChange={(checked: boolean) => setShowTerminatedEmployees(checked)}
          />
          <Text size="200">
            {i18n.t(
              'trainings.trainings_revamp.training_detail.employees_table.add_participants_modal.showTerminated'
            )}
          </Text>
        </Box>
        <Box flexDirection="row" gap="s8">
          <TogglerCheckbox value={toggled} onChange={() => setToggled(!toggled)} />
          <Text size="200">
            {i18n.t(
              'trainings.trainings_revamp.training_detail.employees_table.add_participants_modal.toggle'
            )}
          </Text>
        </Box>
        {toggled && (
          <Selector.Popover
            label={i18n.t(
              'trainings.trainings_revamp.training_detail.employees_table.assign_modal.classes'
            )}
            placeholder={i18n.t(
              'trainings.trainings_revamp.training_detail.employees_table.assign_modal.placeholder'
            )}
            searchPlaceholder={i18n.t('common.search')}
            blankLabel=""
            value={classIds}
            onChange={(selection) => {
              if (Array.isArray(selection) && selection.every((item) => typeof item === 'number')) {
                setClassIds(selection)
              } else {
                throw new Error(
                  `Invalid value for popover selection. Expected a number array. Got: ${typeof selection}`
                )
              }
            }}
            isMultiSelect
            isDisabled={trainingClasses.length === 0}
          >
            {trainingClasses?.map((trainingClass, index) => (
              <Selector.Option
                key={index}
                value={Number(trainingClass.id)}
                label={trainingClass.name}
                searchValue={trainingClass.name}
              >
                {trainingClass.name}
              </Selector.Option>
            ))}
          </Selector.Popover>
        )}
        {!calloutClosed && toggled && trainingClasses.length === 0 && (
          <Callout
            type="info"
            title={i18n.t(
              'trainings.trainings_revamp.training_detail.employees_table.add_participants_modal.callout_title'
            )}
            description={i18n.t(
              'trainings.trainings_revamp.training_detail.employees_table.add_participants_modal.callout_description'
            )}
            hideIcon
            onRemove={() => setCalloutClosed(true)}
          />
        )}
      </Box>
    </Modal>
  )
}
